// WebRtcBusSocket — the second road to a MultiNostalgia server.
//
// It implements the exact same BusSocket surface BusConnection drives
// (bus-connection.ts), so the whole shared-bus stack — riders, coalescing,
// hopping, control frames — runs over it unchanged. The only thing that
// differs from the WebSocket transport is that the unreliable coalescing
// class is delivered unreliably: WebRTC gives us a second, unordered,
// zero-retransmit DataChannel, so `sendUnreliable()` is real here rather
// than degrading to the reliable send.
//
//   reliable class   → an ordered, reliable DataChannel ("reliable"). Carries
//                      the connect handoff (message #1), every reliable game
//                      frame, and the server's TEXT control frames
//                      (peer_left / redirect).
//   unreliable class → an unordered, maxRetransmits:0 DataChannel
//                      ("unreliable"). Position spam that a later frame
//                      supersedes; a dropped datagram is simply skipped.
//
// Both channels ride ONE RTCPeerConnection (one ICE/DTLS/SCTP association) —
// two streams, NOT two connections. Signaling is a single HTTP POST of the
// offer SDP to the same URL the WebSocket transport would have opened
// (scheme swapped ws→http), answered with the server's SDP. Non-trickle ICE:
// we gather candidates fully before POSTing, so one request/response
// completes the handshake with no separate candidate channel.

import type { BusSocket } from "./bus-connection.js";

// WebSocket.readyState values, mirrored so BusConnection's `=== OPEN` checks
// work against this socket without a DOM global at runtime.
const CONNECTING = 0;
const OPEN = 1;
const CLOSED = 3;

// Cap on how long we wait for ICE gathering before POSTing the offer anyway.
// Host candidates gather in milliseconds; this only bounds the tail where a
// configured STUN/TURN server is slow or unreachable (we then signal with
// whatever candidates we have, which for a reachable server is enough).
const ICE_GATHER_TIMEOUT_MS = 3000;

// How long a connection may sit in the transient "disconnected" ICE state before
// we treat it as a real drop. Long enough for a momentary network blip to
// recover (the browser transitions back to "connected"), short enough that a
// genuinely dead link — one that never reaches "failed" on its own — doesn't
// linger. A hard "failed"/"closed" still tears down immediately regardless.
const DISCONNECT_GRACE_MS = 10000;

type Listener = (ev: { data?: unknown }) => void;

// Injectable seams so the signaling/peer machinery is unit-testable with fakes,
// matching the codebase's factory-injection style (BusSocketFactory et al.).
export interface WebRtcBusSocketDeps {
  // Defaults to the DOM `RTCPeerConnection`.
  createPeerConnection?: (config: RTCConfiguration) => RTCPeerConnection;
  // Defaults to the global `fetch`.
  fetchFn?: typeof fetch;
  // ICE servers for the browser side. A public STUN server lets a client
  // behind NAT discover its server-reflexive candidate to reach a public
  // shard; on a LAN/localhost host candidates alone connect. Overridable so a
  // deployment can point at its own STUN/TURN.
  iceServers?: RTCIceServer[];
  // Signaling transport: given our gathered offer SDP, return the peer's answer
  // SDP. Default is a single HTTP POST to the server (scheme swapped ws→http) —
  // correct for a hosted server that can serve HTTP. A P2P joiner overrides this
  // to relay the offer/answer through a broker, since it's connecting to another
  // browser that can't accept an HTTP POST (docs/p2p.md). Everything else — the
  // two channels, the reliability split — is identical; only the SDP's path
  // between the two peers changes.
  signalOffer?: (offerSdp: string) => Promise<string>;
}

const DEFAULT_ICE_SERVERS: RTCIceServer[] = [
  { urls: "stun:stun.l.google.com:19302" },
];

// Send a frame's bytes over a DataChannel. RTCDataChannel's binary overload is
// typed for an ArrayBufferView over a non-shared ArrayBuffer; our frames are
// always regular (non-shared) Uint8Arrays from the envelope codec, so the cast
// is sound — the same `as unknown as` convention used elsewhere for DOM byte
// APIs (e.g. crypto.subtle.digest in main.ts).
function sendBinary(dc: RTCDataChannel, data: Uint8Array): void {
  dc.send(data as unknown as ArrayBuffer);
}

// Swap a ws/wss URL to the http/https URL the server signs / signals on — same
// host, same path, just the scheme. Mirrors wsToHttp in main.ts (kept local so
// this module stands alone as a transport).
function signalingUrl(wsUrl: string): string {
  if (wsUrl.startsWith("wss://")) return "https://" + wsUrl.slice("wss://".length);
  if (wsUrl.startsWith("ws://")) return "http://" + wsUrl.slice("ws://".length);
  return wsUrl;
}

// The default signaling transport: POST the offer SDP to the server and read the
// answer SDP from the response body (the server answers with acceptWebRtcOffer).
// A P2P joiner replaces this with a broker relay (WebRtcBusSocketDeps.signalOffer).
async function httpPostSignal(httpUrl: string, offerSdp: string, fetchFn: typeof fetch): Promise<string> {
  const resp = await fetchFn(httpUrl, {
    method: "POST",
    headers: { "content-type": "application/sdp" },
    body: offerSdp,
  });
  if (!resp.ok) throw new Error(`signaling HTTP ${resp.status}`);
  return resp.text();
}

export class WebRtcBusSocket implements BusSocket {
  readyState = CONNECTING;
  binaryType = "arraybuffer";

  private readonly pc: RTCPeerConnection;
  private readonly reliable: RTCDataChannel;
  private readonly unreliable: RTCDataChannel;
  private readonly listeners = new Map<string, Listener[]>();
  private readonly fetchFn: typeof fetch;
  // Exchange our offer SDP for the peer's answer SDP (see WebRtcBusSocketDeps).
  private readonly signalOffer: (offerSdp: string) => Promise<string>;
  private openEmitted = false;
  // Pending grace-close timer while the connection sits in "disconnected".
  private disconnectTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(url: string, deps: WebRtcBusSocketDeps = {}) {
    const makePc = deps.createPeerConnection ?? ((c) => new RTCPeerConnection(c));
    this.fetchFn = deps.fetchFn ?? ((...a: Parameters<typeof fetch>) => fetch(...a));
    // Default signaling: POST the offer to the server, read the answer back.
    this.signalOffer = deps.signalOffer
      ?? ((offerSdp) => httpPostSignal(signalingUrl(url), offerSdp, this.fetchFn));
    this.pc = makePc({ iceServers: deps.iceServers ?? DEFAULT_ICE_SERVERS });

    // The client is the offerer, so it creates both channels; the server
    // adopts them via its onDataChannel. Labels are the reliability contract
    // between the two ends.
    this.reliable = this.pc.createDataChannel("reliable", { ordered: true });
    this.unreliable = this.pc.createDataChannel("unreliable", {
      ordered: false,
      maxRetransmits: 0,
    });
    this.reliable.binaryType = "arraybuffer";
    this.unreliable.binaryType = "arraybuffer";

    // Inbound: both channels deliver into the one message stream BusConnection
    // demuxes. Binary arrives as ArrayBuffer (game frames), text as string
    // (control frames) — exactly the two shapes the bus already distinguishes.
    const forward = (ev: MessageEvent): void => this.emit("message", { data: ev.data });
    this.reliable.onmessage = forward;
    this.unreliable.onmessage = forward;

    // The reliable channel opening is our "connected" signal — it's the one
    // that carries the handoff and control plane. (The unreliable channel
    // usually opens in the same breath; a send before it's up falls back to
    // the reliable channel in sendUnreliable.)
    this.reliable.onopen = () => this.markOpen();
    this.reliable.onclose = () => this.handleClose();
    this.reliable.onerror = () => this.emit("error", {});

    // A peer connection that fails or closes is a real disconnect — surface it
    // as a socket close so the supervisor tears the bus down the same way a
    // WebSocket close would. But "disconnected" is DIFFERENT: it's usually a
    // transient ICE blip the browser recovers from on its own (the SCTP data
    // channels survive it), and the transport has no reconnect — a fallback only
    // happens at connect time. Tearing down on "disconnected" would kill the
    // session for a momentary hiccup, so instead give it a grace window and
    // close only if it hasn't recovered to a live state.
    this.pc.onconnectionstatechange = () => {
      const st = this.pc.connectionState;
      if (st === "failed" || st === "closed") {
        this.handleClose();
      } else if (st === "disconnected") {
        this.armDisconnectGrace();
      } else {
        // "connected"/"connecting" — recovered (or never left); cancel any
        // pending grace-close.
        this.clearDisconnectGrace();
      }
    };

    void this.signal().catch((e: unknown) => {
      this.emit("error", {});
      if (typeof console !== "undefined") {
        console.error(`[webrtc] signaling failed for ${url}: ${(e as Error).message}`);
      }
      this.handleClose();
    });
  }

  // Perform the offer/answer exchange. Non-trickle: we wait for ICE gathering to
  // finish (bounded) so the offer SDP already carries our candidates, hand it to
  // the signaling transport (HTTP POST to a server, or a broker relay for P2P),
  // and apply the answer it returns.
  private async signal(): Promise<void> {
    const offer = await this.pc.createOffer();
    await this.pc.setLocalDescription(offer);
    await this.iceGatheringComplete();
    const localSdp = this.pc.localDescription?.sdp ?? offer.sdp ?? "";

    const answerSdp = await this.signalOffer(localSdp);
    if (this.readyState === CLOSED) return; // closed mid-handshake
    await this.pc.setRemoteDescription({ type: "answer", sdp: answerSdp });
    // Channels transition to "open" via onopen once the DTLS/SCTP handshake
    // completes; markOpen fires the socket "open" from there.
  }

  private iceGatheringComplete(): Promise<void> {
    if (this.pc.iceGatheringState === "complete") return Promise.resolve();
    return new Promise((resolve) => {
      let done = false;
      const finish = (): void => {
        if (done) return;
        done = true;
        this.pc.removeEventListener("icegatheringstatechange", onChange);
        clearTimeout(timer);
        resolve();
      };
      const onChange = (): void => {
        if (this.pc.iceGatheringState === "complete") finish();
      };
      this.pc.addEventListener("icegatheringstatechange", onChange);
      // Fallback: proceed with whatever we've gathered if "complete" is slow.
      const timer = setTimeout(finish, ICE_GATHER_TIMEOUT_MS);
    });
  }

  // Start (or leave running) the grace window for a "disconnected" connection.
  // If it hasn't recovered to a live state by the deadline, treat it as a drop.
  private armDisconnectGrace(): void {
    if (this.disconnectTimer !== null) return; // already counting down
    this.disconnectTimer = setTimeout(() => {
      this.disconnectTimer = null;
      if (this.pc.connectionState === "disconnected") this.handleClose();
    }, DISCONNECT_GRACE_MS);
  }

  private clearDisconnectGrace(): void {
    if (this.disconnectTimer !== null) {
      clearTimeout(this.disconnectTimer);
      this.disconnectTimer = null;
    }
  }

  private markOpen(): void {
    if (this.openEmitted || this.readyState === CLOSED) return;
    this.openEmitted = true;
    this.readyState = OPEN;
    this.emit("open", {});
  }

  // Reliable/ordered send — game frames of the reliable class, the handoff,
  // and anything the caller passes through send(). Drops if the channel isn't
  // open (matches the WebSocket transport's drop-if-not-open).
  send(data: Uint8Array): void {
    if (this.reliable.readyState !== "open") return;
    sendBinary(this.reliable, data);
  }

  // Unreliable/unordered fast-path. Uses the zero-retransmit channel when it's
  // up; before it opens (a narrow window) falls back to the reliable channel
  // so nothing is silently lost during connection bring-up.
  sendUnreliable(data: Uint8Array): void {
    if (this.unreliable.readyState === "open") {
      sendBinary(this.unreliable, data);
      return;
    }
    if (this.reliable.readyState === "open") sendBinary(this.reliable, data);
  }

  close(): void {
    this.handleClose();
  }

  private handleClose(): void {
    if (this.readyState === CLOSED) return;
    this.readyState = CLOSED;
    this.clearDisconnectGrace();
    try { this.reliable.close(); } catch { /* already gone */ }
    try { this.unreliable.close(); } catch { /* already gone */ }
    try { this.pc.close(); } catch { /* already gone */ }
    this.emit("close", {});
  }

  addEventListener(
    type: "open" | "message" | "close" | "error",
    listener: Listener,
  ): void {
    const arr = this.listeners.get(type);
    if (arr) arr.push(listener);
    else this.listeners.set(type, [listener]);
  }

  private emit(type: string, ev: { data?: unknown }): void {
    const arr = this.listeners.get(type);
    if (!arr) return;
    // Snapshot — a handler may close the socket / mutate the rider set.
    for (const l of [...arr]) l(ev);
  }
}
